import { useEffect, useId, useState, type ReactNode } from "react";
import { NavigationBlocker } from "../../components/NavigationBlocker";
import { useHardConfirm } from "../../components/HardConfirmDialog";
import type {
  CreateUnitInput,
  CreateUnitPeakPricingWindowInput,
  ProviderVenue,
  UnitStatus,
  UnitType,
  RoomActivity,
  RoomAmenity,
} from "../../lib/api";
import { formatPeakPricingWindow } from "../../lib/provider-pricing";
import { useAppPreferences } from "../../lib/preferences";
import { roomCapabilityCopy } from "./room-capability-copy";

const roomActivities: RoomActivity[] = ["console", "pc", "vr", "simulator"];
const roomAmenities: RoomAmenity[] = ["screen", "netflix", "cinema"];

const unitTypeOptions: UnitType[] = [
  "console",
  "pc",
  "vr",
  "simulator",
  "billiards",
  "pingpong",
  "room",
  "other",
];

const unitStatusOptions: UnitStatus[] = [
  "available",
  "reserved",
  "blocked",
  "offline",
];

const dayOptions = [
  { label: "Sunday", value: 0 },
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
];

export const emptyUnitForm: CreateUnitInput = {
  name: "",
  type: "console",
  status: "available",
  base_hourly_rate: 0,
  capacity: 4,
  is_vip: false,
  peak_pricing_windows: [],
};

type UnitFormDraft = Omit<CreateUnitInput, "base_hourly_rate" | "peak_pricing_windows"> & {
  base_hourly_rate: string;
  peak_pricing_windows: Array<
    Omit<CreateUnitPeakPricingWindowInput, "hourly_rate"> & { hourly_rate: string }
  >;
};

function toUnitFormDraft(value: CreateUnitInput): UnitFormDraft {
  return {
    ...value,
    base_hourly_rate: String(value.base_hourly_rate),
    peak_pricing_windows: value.peak_pricing_windows.map((window) => ({
      ...window,
      hourly_rate: String(window.hourly_rate),
    })),
  };
}

function parseRequiredPrice(value: string) {
  const parsed = Number(value.trim());
  return value.trim() && Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

export function UnitFormCard({
  actions,
  description,
  disabled,
  initialValue,
  isSubmitting,
  onDirtyChange,
  onSubmit,
  selectedVenue,
  submitLabel,
  title,
}: {
  actions?: ReactNode;
  description: string;
  disabled?: boolean;
  initialValue: CreateUnitInput;
  isSubmitting: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  onSubmit: (payload: CreateUnitInput) => Promise<void>;
  selectedVenue: ProviderVenue | null;
  submitLabel: string;
  title: string;
}) {
  const { t, resolvedLanguage } = useAppPreferences();
  const roomCopy = roomCapabilityCopy[resolvedLanguage];
  const { confirm, dialog } = useHardConfirm();
  const formId = useId();
  const resourceHintId = `${formId}-resource-hint`;
  const typeLabelId = `${formId}-type-label`;
  const typeHintId = `${formId}-type-hint`;
  const roomGamesHintId = `${formId}-room-games-hint`;
  const [form, setForm] = useState<UnitFormDraft>(() => toUnitFormDraft(initialValue));
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setForm(toUnitFormDraft(initialValue));
  }, [initialValue]);

  useEffect(() => {
    const dirty =
      form.name !== initialValue.name ||
      form.type !== initialValue.type ||
      form.status !== initialValue.status ||
      form.base_hourly_rate !== String(initialValue.base_hourly_rate) ||
      form.capacity !== initialValue.capacity ||
      form.is_vip !== initialValue.is_vip ||
      JSON.stringify(form.supported_activities ?? null) !== JSON.stringify(initialValue.supported_activities ?? null) ||
      JSON.stringify(form.amenities ?? null) !== JSON.stringify(initialValue.amenities ?? null) ||
      form.peak_pricing_windows.length !== initialValue.peak_pricing_windows.length ||
      form.peak_pricing_windows.some((window, index) => {
        const initialWindow = initialValue.peak_pricing_windows[index];
        if (!initialWindow) return true;
        return (
          window.day_of_week !== initialWindow.day_of_week ||
          window.start_time !== initialWindow.start_time ||
          window.end_time !== initialWindow.end_time ||
          window.hourly_rate !== String(initialWindow.hourly_rate)
        );
      });
    setIsDirty(dirty);
    onDirtyChange?.(dirty);
  }, [form, initialValue, onDirtyChange]);

  const updateField = <Field extends keyof UnitFormDraft>(
    field: Field,
    value: UnitFormDraft[Field],
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updatePeakPricingWindow = <Field extends keyof UnitFormDraft["peak_pricing_windows"][number]>(
    index: number,
    field: Field,
    value: UnitFormDraft["peak_pricing_windows"][number][Field],
  ) => {
    setForm((current) => ({
      ...current,
      peak_pricing_windows: current.peak_pricing_windows.map((window, windowIndex) =>
        windowIndex === index ? { ...window, [field]: value } : window,
      ),
    }));
  };

  const addPeakPricingWindow = () => {
    setForm((current) => ({
      ...current,
      peak_pricing_windows: current.peak_pricing_windows.concat({
        day_of_week: 0,
        start_time: "18:00",
        end_time: "20:00",
        hourly_rate: current.base_hourly_rate,
      }),
    }));
  };

  const removePeakPricingWindow = (index: number) => {
    setForm((current) => ({
      ...current,
      peak_pricing_windows: current.peak_pricing_windows.filter(
        (_, windowIndex) => windowIndex !== index,
      ),
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const baseHourlyRate = parseRequiredPrice(form.base_hourly_rate);
    const peakPricingWindows = form.peak_pricing_windows.map((window) => ({
      ...window,
      hourly_rate: parseRequiredPrice(window.hourly_rate),
    }));
    if (baseHourlyRate === null || peakPricingWindows.some((window) => window.hourly_rate === null)) {
      return;
    }
    const payload: CreateUnitInput = {
      ...form,
      base_hourly_rate: baseHourlyRate,
      peak_pricing_windows: peakPricingWindows as CreateUnitPeakPricingWindowInput[],
    };
    const optionsChanged = JSON.stringify(form.supported_activities ?? null) !== JSON.stringify(initialValue.supported_activities ?? null)
      || JSON.stringify(form.amenities ?? null) !== JSON.stringify(initialValue.amenities ?? null);
    if (!optionsChanged || form.type !== "room") {
      delete payload.supported_activities;
      delete payload.amenities;
      delete payload.expected_capability_revision;
    }
    if (form.type !== "room" && ((initialValue.supported_activities?.length ?? 0) > 0 || (initialValue.amenities?.length ?? 0) > 0)) {
      payload.supported_activities = [];
      payload.amenities = [];
      payload.expected_capability_revision = initialValue.expected_capability_revision;
    }
    await onSubmit(payload);
  };

  const handleRemovePeakPricingWindow = async (
    index: number,
    window: UnitFormDraft["peak_pricing_windows"][number],
  ) => {
    const isConfirmed = await confirm({
      confirmationValue: "REMOVE",
      confirmButtonLabel: t("Discard window"),
      consequence: t("This only removes the unsaved pricing row from the current draft until you add it again."),
      description: t("This removes the draft pricing window for {window}.", {
        window: formatPeakPricingWindow({ ...window, hourly_rate: Number(window.hourly_rate) || 0 }),
      }),
      title: t("Discard this peak pricing window?"),
    });
    if (!isConfirmed) {
      return;
    }

    removePeakPricingWindow(index);
  };

  const formBody = (
    <div className="grid gap-3">
      <p className="ops-copy text-sm" id={resourceHintId}>{t("setup.units.resourceHint")}</p>
      <div className="grid items-start gap-3 md:grid-cols-2">
        <label className="ops-field">
          <span className="ops-label">{t("Unit name")}</span>
          <input
            aria-describedby={resourceHintId}
            className="ops-input"
            disabled={disabled}
            maxLength={255}
            onChange={(event) => updateField("name", event.target.value)}
            placeholder={t("PS5 Arena")}
            required
            value={form.name}
          />
        </label>
        <label className="ops-field">
          <span className="ops-label" id={typeLabelId}>{t("Unit type")}</span>
          <select
            aria-describedby={`${resourceHintId} ${typeHintId}${form.type === "room" ? ` ${roomGamesHintId}` : ""}`}
            aria-labelledby={typeLabelId}
            className="ops-select"
            disabled={disabled}
            onChange={(event) => updateField("type", event.target.value as UnitType)}
            value={form.type}
          >
            {unitTypeOptions.map((type) => (
              <option key={type} value={type}>
                {t(type.replaceAll("_", " "))}
              </option>
            ))}
          </select>
          <span className="ops-copy text-xs leading-5" id={typeHintId}>
            {t("setup.units.typeHint", { roomType: t("room"), otherType: t("other") })}
          </span>
        </label>
      </div>
      {form.type === "room" && (
        <div className="ops-subtle-surface p-3">
          <p className="ops-copy text-xs leading-5" id={roomGamesHintId}>{t("setup.units.roomGamesHint")}</p>
        </div>
      )}
      {form.type !== "room" && ((initialValue.supported_activities?.length ?? 0) > 0 || (initialValue.amenities?.length ?? 0) > 0) &&
        <p role="status" className="ops-copy text-sm">{resolvedLanguage === "ar"
          ? "تغيير نوع الغرفة هيمسح اختياراتها المشمولة. الحجز والسعر والصور بيفضلوا مرتبطين بنفس الوحدة."
          : "Changing the room type clears its included options. Bookings, rates and photos remain linked to this same unit."}</p>}
      {form.type === "room" && <fieldset className="grid gap-3 border-t border-[var(--color-border)] pt-3">
        <legend className="ops-label">{roomCopy.title}</legend>
        <p className="ops-copy text-xs leading-5">{roomCopy.included}</p>
        <label className="ops-checkbox-row">
          <input type="checkbox" disabled={disabled || isSubmitting}
            checked={form.supported_activities != null || form.amenities != null}
            onChange={event => setForm(current => ({ ...current,
              supported_activities: event.target.checked ? current.supported_activities ?? [] : null,
              amenities: event.target.checked ? current.amenities ?? [] : null,
            }))} />
          <span>{roomCopy.reviewed}</span>
        </label>
        {form.supported_activities == null && form.amenities == null ? <p className="ops-copy text-xs">{roomCopy.unknown}</p> : <>
          <fieldset className="grid gap-2 sm:grid-cols-2">
            <legend className="ops-label mb-2">{roomCopy.activities}</legend>
            {roomActivities.map(activity => <label className="ops-checkbox-row" key={activity}>
              <input type="checkbox" disabled={disabled || isSubmitting} checked={form.supported_activities?.includes(activity) ?? false}
                onChange={event => updateField("supported_activities", event.target.checked
                  ? [...(form.supported_activities ?? []), activity] : (form.supported_activities ?? []).filter(value => value !== activity))} />
              <span>{roomCopy[activity]}</span>
            </label>)}
          </fieldset>
          <fieldset className="grid gap-2 sm:grid-cols-2">
            <legend className="ops-label mb-2">{roomCopy.amenities}</legend>
            {roomAmenities.map(amenity => <label className="ops-checkbox-row" key={amenity}>
              <input type="checkbox" disabled={disabled || isSubmitting} checked={form.amenities?.includes(amenity) ?? false}
                onChange={event => updateField("amenities", event.target.checked
                  ? [...(form.amenities ?? []), amenity] : (form.amenities ?? []).filter(value => value !== amenity))} />
              <span>{roomCopy[amenity]}</span>
            </label>)}
          </fieldset>
        </>}
      </fieldset>}
      <div className="grid gap-3 md:grid-cols-2">
        <label className="ops-field">
          <span className="ops-label">{t("Status")}</span>
          <select
            className="ops-select"
            disabled={disabled}
            onChange={(event) => updateField("status", event.target.value as UnitStatus)}
            value={form.status}
          >
            {unitStatusOptions.map((status) => (
              <option key={status} value={status}>
                {t(status.replaceAll("_", " "))}
              </option>
            ))}
          </select>
        </label>
        <label className="ops-field">
          <span className="ops-label">{t("Capacity")}</span>
          <input
            className="ops-input"
            disabled={disabled}
            min={1}
            max={100}
            onChange={(event) => updateField("capacity", Number(event.target.value))}
            required
            type="number"
            value={form.capacity}
          />
        </label>
      </div>
      <label className="ops-field">
        <span className="ops-label">{t("Base hourly rate")}</span>
        <input
          className="ops-input"
          disabled={disabled}
          min={0}
          max={1000000}
          onChange={(event) =>
            updateField("base_hourly_rate", event.target.value)
          }
          required
          step="0.01"
          type="number"
          value={form.base_hourly_rate}
        />
      </label>
      <div className="ops-subtle-surface grid gap-2 p-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="ops-label">{t("Peak pricing windows")}</p>
            <p className="text-xs leading-4 text-slate-400">
              {t("Override the base rate during specific venue-local hours.")}
              {selectedVenue ? ` ${t("{timezone} local time.", { timezone: selectedVenue.timezone })}` : ""}
            </p>
          </div>
          <button
            className="ops-button"
            data-size="sm"
            data-variant="secondary"
            disabled={disabled}
            onClick={addPeakPricingWindow}
            type="button"
          >
            {t("Add peak window")}
          </button>
        </div>

        {form.peak_pricing_windows.length === 0 ? (
          <p className="text-xs leading-4 text-slate-500">
            {t("No peak pricing yet. Add a window to raise rates during busy hours.")}
          </p>
        ) : (
          <div className="grid gap-2">
            {form.peak_pricing_windows.map((window, index) => (
              <div
                className="ops-record ops-record-dense grid items-end gap-2 sm:grid-cols-[1fr,1fr,1fr,1fr,auto]"
                key={`${window.day_of_week}-${window.start_time}-${window.end_time}-${index}`}
              >
                <label className="ops-field">
                  <span className="ops-label">{t("Day")}</span>
                  <select
                    className="ops-select"
                    disabled={disabled}
                    onChange={(event) =>
                      updatePeakPricingWindow(index, "day_of_week", Number(event.target.value))
                    }
                    value={window.day_of_week}
                  >
                    {dayOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(option.label)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="ops-field">
                  <span className="ops-label">{t("Starts")}</span>
                  <input
                    className="ops-input"
                    disabled={disabled}
                    onChange={(event) =>
                      updatePeakPricingWindow(index, "start_time", event.target.value)
                    }
                    required
                    step={60}
                    type="time"
                    value={window.start_time}
                  />
                </label>
                <label className="ops-field">
                  <span className="ops-label">{t("Ends")}</span>
                  <input
                    className="ops-input"
                    disabled={disabled}
                    onChange={(event) =>
                      updatePeakPricingWindow(index, "end_time", event.target.value)
                    }
                    required
                    step={60}
                    type="time"
                    value={window.end_time}
                  />
                </label>
                <label className="ops-field">
                  <span className="ops-label">{t("Rate")}</span>
                  <input
                    className="ops-input"
                    disabled={disabled}
                    min={0}
                    max={1000000}
                    onChange={(event) =>
                      updatePeakPricingWindow(
                        index,
                        "hourly_rate",
                        event.target.value,
                      )
                    }
                    required
                    step="0.01"
                    type="number"
                    value={window.hourly_rate}
                  />
                </label>
                <button
                  className="ops-button self-end"
                  data-size="sm"
                  data-variant="danger"
                  disabled={disabled}
                  onClick={() => void handleRemovePeakPricingWindow(index, window)}
                  type="button"
                >
                  {t("Remove")}
                </button>
                <p className="col-span-full text-[0.6rem] uppercase tracking-[0.14em] text-slate-500">
                  {formatPeakPricingWindow({
                    ...window,
                    hourly_rate: Number(window.hourly_rate) || 0,
                  })}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
      <label className="ops-checkbox-row !py-2.5 !px-3">
        <input
          checked={form.is_vip}
          className="size-4 rounded border-white/20 bg-slate-900 text-emerald-300"
          disabled={disabled}
          onChange={(event) => updateField("is_vip", event.target.checked)}
          type="checkbox"
        />
        <span className="text-sm">{t("Mark as VIP unit")}</span>
      </label>
      <button
        className="ops-button w-full"
        data-size="sm"
        data-variant="primary"
        disabled={isSubmitting || disabled || !selectedVenue}
        type="submit"
      >
        {isSubmitting ? `${submitLabel}...` : submitLabel}
      </button>
    </div>
  );

  return (
    <>
      <NavigationBlocker isDirty={isDirty} />
      <form
        className={`ops-surface ${actions ? "lg:grid lg:grid-cols-[1fr,16rem] lg:gap-4" : ""}`}
        onSubmit={(event) => void handleSubmit(event)}
      >
        {dialog}

        <div>
          <p className="ops-kicker">{title}</p>
          <p className="mt-2 ops-copy">{description}</p>
          <div className="mt-4">
            {formBody}
          </div>
        </div>

        {actions && (
          <div className="mt-4 lg:mt-0">
            {actions}
          </div>
        )}
      </form>
    </>
  );
}
